import React, { useState } from 'react';
import { api } from '../utils/api';

const STEPS = { auth: 'auth', telegram: 'telegram' };

export const AuthPage = ({ onLogin }) => {
  const [step,     setStep]     = useState(STEPS.auth);
  const [mode,     setMode]     = useState('login');
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [botToken, setBotToken] = useState('');
  const [chatId,   setChatId]   = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [userData, setUserData] = useState(null);

  // Step 1 — Login or Register
  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const body = mode === 'register'
        ? { name, email, password }
        : { email, password };
      const res = mode === 'register'
        ? await api.register(body)
        : await api.login(body);

      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.data));
      setUserData(res.data);

      // New registration → go to Telegram setup
      // Existing login with no telegram → go to Telegram setup
      // Existing login with telegram set → go to dashboard
      if (mode === 'register' || !res.data.telegramChatId) {
        setStep(STEPS.telegram);
      } else {
        onLogin(res.data, res.token);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — Telegram setup
  const handleTelegram = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Test the bot token first
      const testRes = await fetch(
        `https://api.telegram.org/bot${botToken}/sendMessage?chat_id=${chatId}&text=✅ Your Solana Hub alerts are now active!`
      );
      const testData = await testRes.json();
      if (!testData.ok) {
        setError(`Telegram error: ${testData.description}. Check your token and chat ID.`);
        setLoading(false);
        return;
      }

      // Save to backend
      await api.updateTelegram({ telegramBotToken: botToken, telegramChatId: chatId });

      // Update local user data
      const updatedUser = { ...userData, telegramChatId: chatId };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      onLogin(updatedUser);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Skip telegram setup (optional)
  const handleSkip = () => {
    onLogin(userData);
  };

  if (step === STEPS.telegram) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.logo}>📱</div>
          <h1 style={S.title}>Setup Telegram</h1>
          <p style={S.sub}>
            You need your own Telegram bot to receive alerts privately.
            Your alerts will go only to you — no one else can see them.
          </p>

          <div style={S.steps}>
            <div style={S.step}>
              <span style={S.stepNum}>1</span>
              <span>Open Telegram → search <strong>@BotFather</strong> → send <code>/newbot</code></span>
            </div>
            <div style={S.step}>
              <span style={S.stepNum}>2</span>
              <span>Copy the bot token BotFather gives you</span>
            </div>
            <div style={S.step}>
              <span style={S.stepNum}>3</span>
              <span>Search <strong>@userinfobot</strong> → send <code>/start</code> → copy your ID</span>
            </div>
            <div style={S.step}>
              <span style={S.stepNum}>4</span>
              <span>Start your new bot by searching its username and tapping Start</span>
            </div>
          </div>

          <form onSubmit={handleTelegram} style={S.form}>
            <div style={S.field}>
              <label>Your Bot Token</label>
              <input value={botToken} onChange={e => setBotToken(e.target.value)}
                placeholder="123456789:ABCDEFGHijklmno..." required />
            </div>
            <div style={S.field}>
              <label>Your Chat ID</label>
              <input value={chatId} onChange={e => setChatId(e.target.value)}
                placeholder="e.g. 1154323353" required />
            </div>

            {error && <div style={S.error}>❌ {error}</div>}

            <button type="submit" style={S.btn} disabled={loading}>
              {loading ? '⏳ Testing & saving…' : '✓ Activate Alerts'}
            </button>

            <button type="button" style={S.skipBtn} onClick={handleSkip}>
              Skip for now (you can set this up later in Settings)
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>◎</div>
        <h1 style={S.title}>SOL HUB</h1>
        <p style={S.sub}>Solana Automation Platform</p>

        <div style={S.tabs}>
          <button style={{ ...S.tab, ...(mode === 'login' ? S.tabActive : {}) }}
            onClick={() => { setMode('login'); setError(''); }}>
            Login
          </button>
          <button style={{ ...S.tab, ...(mode === 'register' ? S.tabActive : {}) }}
            onClick={() => { setMode('register'); setError(''); }}>
            Register
          </button>
        </div>

        <form onSubmit={handleAuth} style={S.form}>
          {mode === 'register' && (
            <div style={S.field}>
              <label>Full Name</label>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="Your name" required />
            </div>
          )}
          <div style={S.field}>
            <label>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@email.com" required />
          </div>
          <div style={S.field}>
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••" required minLength={6} />
          </div>

          {error && <div style={S.error}>❌ {error}</div>}

          <button type="submit" style={S.btn} disabled={loading}>
            {loading ? '⏳ Please wait…' : mode === 'login' ? '→ Login' : '→ Create Account'}
          </button>
        </form>

        {mode === 'register' && (
          <p style={S.planNote}>
            🆓 Free plan: track up to 3 wallets · Upgrade anytime for unlimited
          </p>
        )}
      </div>
    </div>
  );
};

const S = {
  page:     { minHeight:'100vh', display:'flex', alignItems:'center',
              justifyContent:'center', padding:24 },
  card:     { background:'#0d1117', border:'1px solid #1e2d3d', borderRadius:16,
              padding:'40px 36px', width:'100%', maxWidth:460, textAlign:'center' },
  logo:     { fontSize:48, color:'#00ff88', lineHeight:1, marginBottom:8,
              textShadow:'0 0 30px #00ff8860' },
  title:    { fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:28,
              letterSpacing:'0.12em', margin:'0 0 4px' },
  sub:      { color:'#7a94b0', fontSize:13, marginBottom:24, lineHeight:1.6 },
  tabs:     { display:'flex', background:'#080b10', borderRadius:8,
              padding:4, marginBottom:28 },
  tab:      { flex:1, background:'transparent', color:'#7a94b0', padding:'8px 0',
              borderRadius:6, fontSize:14, fontWeight:600, border:'none', cursor:'pointer' },
  tabActive:{ background:'#00ff8820', color:'#00ff88' },
  form:     { display:'flex', flexDirection:'column', gap:16, textAlign:'left' },
  field:    { display:'flex', flexDirection:'column', gap:6 },
  error:    { background:'#ff4d6a15', border:'1px solid #ff4d6a40', borderRadius:8,
              padding:'10px 14px', color:'#ff4d6a', fontSize:13, textAlign:'center' },
  btn:      { background:'#00ff88', color:'#080b10', padding:'13px', fontWeight:700,
              fontSize:15, borderRadius:8, marginTop:4, border:'none', cursor:'pointer' },
  skipBtn:  { background:'transparent', color:'#7a94b0', border:'none',
              fontSize:12, cursor:'pointer', textDecoration:'underline', marginTop:4 },
  planNote: { marginTop:20, fontSize:12, color:'#7a94b0', lineHeight:1.6 },
  steps:    { display:'flex', flexDirection:'column', gap:12,
              marginBottom:24, textAlign:'left' },
  step:     { display:'flex', alignItems:'flex-start', gap:12,
              fontSize:13, color:'#7a94b0', lineHeight:1.5 },
  stepNum:  { background:'#00ff8820', color:'#00ff88', border:'1px solid #00ff8840',
              borderRadius:'50%', width:24, height:24, display:'flex',
              alignItems:'center', justifyContent:'center',
              fontSize:11, fontWeight:700, flexShrink:0 },
};
